import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp, onSnapshot, collection, query, where, orderBy, limit as limitQuery, getDocs } from 'firebase/firestore';

/**
 * RSK Currency Service
 * Manages user's RSK (reward currency) balance and transactions
 */

export const rskService = {
  /**
   * Get user's current RSK balance
   */
  async getUserBalance(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        return { balance: data.rskBalance || 0, error: null };
      } else {
        // Initialize balance if user document exists but no RSK balance
        await this.initializeBalance(userId);
        return { balance: 0, error: null };
      }
    } catch (error) {
      console.error('Error getting RSK balance:', error);
      return { balance: 0, error: error.message };
    }
  },

  /**
   * Initialize RSK balance for new users
   */
  async initializeBalance(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        rskBalance: 0,
        lastRskUpdate: serverTimestamp(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error initializing RSK balance:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Subscribe to real-time RSK balance updates
   */
  subscribeToBalance(userId, callback) {
    const userRef = doc(db, 'users', userId);
    
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const balance = data.rskBalance || 0;
        callback(balance);
      }
    }, (error) => {
      console.error('Error in RSK balance subscription:', error);
    });
    
    return unsubscribe;
  },

  /**
   * Award RSK to user
   */
  async awardRSK(userId, amount, reason = 'Unknown') {
    try {
      console.log(`[RSK Service] Awarding ${amount} RSK to user ${userId} for: ${reason}`);
      
      const userRef = doc(db, 'users', userId);
      
      // Update user balance
      await updateDoc(userRef, {
        rskBalance: increment(amount),
        lastRskUpdate: serverTimestamp(),
      });

      // Log the transaction
      const transactionRef = doc(db, 'rsk_transactions', `${userId}_${Date.now()}`);
      await setDoc(transactionRef, {
        userId,
        amount,
        reason,
        type: amount > 0 ? 'credit' : 'debit',
        timestamp: serverTimestamp(),
      });

      // Get new balance
      const { balance } = await this.getUserBalance(userId);
      
      return { success: true, newBalance: balance, error: null };
    } catch (error) {
      console.error('Error awarding RSK:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Deduct RSK from user (for wrong answers or purchases)
   */
  async deductRSK(userId, amount, reason = 'Unknown') {
    return await this.awardRSK(userId, -Math.abs(amount), reason);
  },

  /**
   * Get user's RSK transaction history
   */
  async getUserTransactions(userId, limitCount = 50) {
    try {
      const transactionsRef = collection(db, 'rsk_transactions');
      const transactionsQuery = query(
        transactionsRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limitQuery(limitCount)
      );
      
      const querySnapshot = await getDocs(transactionsQuery);
      const transactions = [];
      
      querySnapshot.forEach((doc) => {
        transactions.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { transactions, error: null };
    } catch (error) {
      console.error('Error getting RSK transactions:', error);
      return { transactions: [], error: error.message };
    }
  },

  /**
   * Award RSK for module completion
   */
  async awardModuleCompletion(userId, moduleTitle) {
    return await this.awardRSK(userId, 10, `Completed module: ${moduleTitle}`);
  },

  /**
   * Manually set RSK balance (for debugging/corrections)
   */
  async setBalance(userId, newBalance, reason = 'Manual adjustment') {
    try {
      console.log(`[RSK Service] Setting balance to ${newBalance} for user ${userId}`);
      
      const userRef = doc(db, 'users', userId);
      
      // Set the exact balance
      await updateDoc(userRef, {
        rskBalance: newBalance,
        lastRskUpdate: serverTimestamp(),
      });
      
      // Log the transaction
      const transactionRef = doc(db, 'rsk_transactions', `${userId}_${Date.now()}_manual`);
      await setDoc(transactionRef, {
        userId,
        amount: newBalance,
        reason,
        type: 'manual_set',
        timestamp: serverTimestamp(),
      });
      
      return { success: true, newBalance, error: null };
    } catch (error) {
      console.error('Error setting RSK balance:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Award RSK for daily decision answer
   */
  async awardDecisionAnswer(userId, answerType, questionTitle) {
    const rskValues = {
      'best': 100,
      'better': 50,
      'good': 10,
      'bad': -10,
      'worse': -50,
      'worst': -100,
    };

    const amount = rskValues[answerType] || 0;
    const action = amount > 0 ? 'Correct' : 'Incorrect';
    
    return await this.awardRSK(
      userId, 
      amount, 
      `${action} daily decision: ${questionTitle}`
    );
  },
};